import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { ConflictNotice } from '../ui/ConflictNotice';
import { NumberInput } from '../ui/NumberInput';
import { formatDecimal, parseScaled } from '../ui/numbers';
import { SETTLEMENTS_KEY, SETTLEMENTS_SETTINGS_RESOURCE } from './types';
import { useUnsavedGuard } from '../ui/unsaved';

type Settings = { standardDayHoursMilli: number; version: number };

/** "Finanse → Ustawienia → Rozliczenia ludzi": the hours of a working day for overtime (D15, default 10). */
export function SettlementsSettings({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: [...SETTLEMENTS_KEY, 'settings'], queryFn: () => api<Settings>('/api/settlements/settings') });
  const [hours, setHours] = useState('');
  const [local, setLocal] = useState('');
  const [saved, setSaved] = useState(false);
  const s = settings.data;
  useEffect(() => {
    if (s) setHours(formatDecimal(s.standardDayHoursMilli / 1000));
  }, [s]);
  const save = useMutation({
    mutationFn: (standardDayHoursMilli: number) => api('/api/settlements/settings', { method: 'PATCH', body: { standardDayHoursMilli, version: s!.version } }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: SETTLEMENTS_KEY });
    },
  });
  const editable = can(me, SETTLEMENTS_SETTINGS_RESOURCE, 'edit');
  function milliOf(): number | null {
    if (!s) return null;
    const milli = parseScaled(hours, 3);
    if (milli === null || milli < 1000 || milli > 24000) return (setLocal('Godziny dnia pracy: od 1 do 24'), null);
    setLocal('');
    setSaved(false);
    return milli;
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const milli = milliOf();
    if (milli !== null && milli !== s?.standardDayHoursMilli) save.mutate(milli);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: editable && !!s && hours !== formatDecimal(s.standardDayHoursMilli / 1000),
    save: async () => {
      const milli = milliOf();
      if (milli === null) return false;
      if (milli !== s?.standardDayHoursMilli) await save.mutateAsync(milli);
      return true;
    },
  });
  const conflict = save.error instanceof ApiError && save.error.status === 409;
  const error = local || (save.error && !conflict ? (save.error instanceof ApiError ? (fieldIssues(save.error)[0]?.message ?? save.error.message) : 'Nie udało się zapisać') : '');
  return (
    <section aria-label="Rozliczenia ludzi — ustawienia" data-feature-id="settlements.settings">
      <h2>Rozliczenia ludzi</h2>
      <p className="lede">Stawka godzinowa nadgodzin = dniówka / godziny dnia pracy.</p>
      {settings.error && <p className="alert" role="alert">{settings.error instanceof ApiError ? settings.error.message : 'Nie udało się wczytać ustawień'}</p>}
      {s && (
        <form ref={guardRef} className="form fin-settings" aria-label="Godziny dnia pracy" onSubmit={submit}>
          {conflict && (
            <ConflictNotice
              onReload={() => {
                save.reset();
                void settings.refetch();
              }}
            />
          )}
          <label className="field">
            <span>Godziny dnia pracy</span>
            <NumberInput value={hours} onValueChange={setHours} disabled={!editable} />
          </label>
          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}
          {saved && <p role="status">Zapisano.</p>}
          {editable && (
            <div className="actions">
              <button className="btn primary" type="submit" disabled={save.isPending}>
                Zapisz
              </button>
            </div>
          )}
        </form>
      )}
    </section>
  );
}
